import React, { useState, useEffect } from 'react';
import { Task, WorkPlan, PlannedWorkDay } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Plus, 
  Save, 
  X, 
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { parseDateLike, toDateKey } from '../utils/dateUtils';
import { generateId } from '../utils/idGenerator';

interface WorkPlanningCalendarProps {
  task: Task;
  currentUserId: string;
  currentUserName: string;
  onUpdate: (workPlan: WorkPlan) => void;
}

export default function WorkPlanningCalendar({
  task,
  currentUserId,
  currentUserName,
  onUpdate,
}: WorkPlanningCalendarProps) {
  const [workPlan, setWorkPlan] = useState<WorkPlan>(
    task.workPlan || {
      id: generateId('wp', task.id, currentUserId),
      taskId: task.id,
      userId: currentUserId,
      userName: currentUserName,
      plannedDays: [],
      createdAt: new Date().toISOString(),
    }
  );

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [hoursForDate, setHoursForDate] = useState<string>('');
  const [dateNotes, setDateNotes] = useState<string>('');
  const [quickDistributeHours, setQuickDistributeHours] = useState<string>('');
  const [quickDistributeDays, setQuickDistributeDays] = useState<string>('7');
  const [showQuickDistribute, setShowQuickDistribute] = useState(false);

  // Generate calendar dates (current week + next 4 weeks = 35 days)
  const generateCalendarDates = () => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 35; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const calendarDates = generateCalendarDates();

  // Get total planned hours
  const totalPlannedHours = workPlan.plannedDays.reduce((sum, day) => sum + day.hours, 0);
  
  // Get total actual hours (from time logs)
  const totalActualHours = task.timeLogs.reduce((sum, log) => sum + log.hours, 0);
  
  // Remaining hours to plan
  const estimatedHours = task.estimatedHours || 0;
  const remainingToPlan = estimatedHours - totalPlannedHours;

  const getHoursForDateKey = (dateKey: string): PlannedWorkDay | undefined => {
    return workPlan.plannedDays.find(day => toDateKey(day.date) === dateKey);
  };

  // Get hours for a specific date
  const getHoursForDate = (date: Date): PlannedWorkDay | undefined => {
    const dateStr = toDateKey(date);
    return dateStr ? getHoursForDateKey(dateStr) : undefined;
  };

  // Get actual hours logged for a specific date
  const getActualHoursForDate = (date: Date): number => {
    const dateStr = toDateKey(date);
    if (!dateStr) return 0;

    return task.timeLogs
      .filter(log => toDateKey(log.date) === dateStr)
      .reduce((sum, log) => sum + log.hours, 0);
  };

  // Check if date has time logged
  const hasTimeLogged = (date: Date): boolean => {
    return getActualHoursForDate(date) > 0;
  };

  // Add/Update hours for a date
  const handleAddHours = () => {
    if (!selectedDate || !hoursForDate) {
      toast.error('Please select a date and enter hours');
      return;
    }

    const hours = parseFloat(hoursForDate);
    if (hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }

    const existingDayIndex = workPlan.plannedDays.findIndex(day => toDateKey(day.date) === selectedDate);
    
    let updatedPlannedDays = [...workPlan.plannedDays];
    
    if (existingDayIndex >= 0) {
      // Update existing day
      updatedPlannedDays[existingDayIndex] = {
        ...updatedPlannedDays[existingDayIndex],
        hours,
        notes: dateNotes,
      };
    } else {
      // Add new day
      const newDay: PlannedWorkDay = {
        date: selectedDate,
        hours,
        notes: dateNotes,
        completed: false,
      };
      updatedPlannedDays.push(newDay);
    }

    // Sort by date
    updatedPlannedDays.sort((a, b) => (toDateKey(a.date) || '').localeCompare(toDateKey(b.date) || ''));

    const updatedWorkPlan = {
      ...workPlan,
      plannedDays: updatedPlannedDays,
      updatedAt: new Date().toISOString(),
    };

    setWorkPlan(updatedWorkPlan);
    onUpdate(updatedWorkPlan);
    setSelectedDate(null);
    setHoursForDate('');
    setDateNotes('');
    toast.success('Work plan updated');
  };

  // Remove hours for a date
  const handleRemoveDay = (date: string) => {
    const updatedPlannedDays = workPlan.plannedDays.filter(day => toDateKey(day.date) !== date);
    const updatedWorkPlan = {
      ...workPlan,
      plannedDays: updatedPlannedDays,
      updatedAt: new Date().toISOString(),
    };

    setWorkPlan(updatedWorkPlan);
    onUpdate(updatedWorkPlan);
    toast.success('Day removed from plan');
  };

  // Quick distribute hours across days
  const handleQuickDistribute = () => {
    const hours = parseFloat(quickDistributeHours);
    const days = parseInt(quickDistributeDays);

    if (!hours || hours <= 0) {
      toast.error('Please enter valid hours');
      return;
    }

    if (!days || days <= 0 || days > 30) {
      toast.error('Days must be between 1 and 30');
      return;
    }

    // Distribute hours evenly across workdays (Mon-Fri) in the next N days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const workdays: string[] = [];
    let currentDate = new Date(today);
    
    while (workdays.length < days) {
      const dayOfWeek = currentDate.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const dateKey = toDateKey(currentDate);
        if (dateKey) {
          workdays.push(dateKey);
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const hoursPerDay = hours / workdays.length;

    const newPlannedDays: PlannedWorkDay[] = workdays.map(date => ({
      date,
      hours: Math.round(hoursPerDay * 2) / 2, // Round to nearest 0.5
      notes: `Auto-distributed: ${hours}h over ${days} workdays`,
      completed: false,
    }));

    // Merge with existing days (don't overwrite)
    const existingDates = new Set(
      workPlan.plannedDays
        .map(d => toDateKey(d.date))
        .filter((dateKey): dateKey is string => Boolean(dateKey))
    );
    const mergedDays = [
      ...workPlan.plannedDays,
      ...newPlannedDays.filter(d => !existingDates.has(d.date)),
    ].sort((a, b) => (toDateKey(a.date) || '').localeCompare(toDateKey(b.date) || ''));

    const updatedWorkPlan = {
      ...workPlan,
      plannedDays: mergedDays,
      updatedAt: new Date().toISOString(),
    };

    setWorkPlan(updatedWorkPlan);
    onUpdate(updatedWorkPlan);
    setQuickDistributeHours('');
    setShowQuickDistribute(false);
    toast.success(`Distributed ${hours}h across ${workdays.length} workdays`);
  };

  // Update completed status based on actual time logs
  useEffect(() => {
    const updatedPlannedDays = workPlan.plannedDays.map(day => {
      const dayDateKey = toDateKey(day.date);
      const actualHours = task.timeLogs
        .filter(log => dayDateKey !== null && toDateKey(log.date) === dayDateKey)
        .reduce((sum, log) => sum + log.hours, 0);

      return {
        ...day,
        actualHours,
        completed: actualHours >= day.hours,
      };
    });

    const hasChanges = updatedPlannedDays.some((day, i) => 
      day.completed !== workPlan.plannedDays[i]?.completed ||
      day.actualHours !== workPlan.plannedDays[i]?.actualHours
    );

    if (hasChanges) {
      setWorkPlan({ ...workPlan, plannedDays: updatedPlannedDays });
    }
  }, [task.timeLogs]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateLong = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            Work Planning Calendar
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowQuickDistribute(!showQuickDistribute)}
          >
            <Zap className="w-4 h-4 mr-2" />
            Quick Distribute
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Distribute Panel */}
        {showQuickDistribute && (
          <Card className="bg-gradient-to-r from-chart-4/25 to-primary/10 border-chart-4/40">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-chart-4" />
                <h4 className="font-semibold text-chart-4">Quick Distribute Hours</h4>
              </div>
              <p className="text-sm text-chart-4">
                Automatically distribute hours across upcoming workdays (Mon-Fri)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Total Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                    value={quickDistributeHours}
                    onChange={(e) => setQuickDistributeHours(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Over Next N Days</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    placeholder="e.g., 7"
                    value={quickDistributeDays}
                    onChange={(e) => setQuickDistributeDays(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleQuickDistribute}
                  className="flex-1"
                  disabled={!quickDistributeHours || !quickDistributeDays}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Distribute
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowQuickDistribute(false)}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-primary/10 rounded-lg p-3 text-center">
            <div className="text-xs text-primary font-semibold">Estimated</div>
            <div className="text-2xl font-bold text-primary">{estimatedHours}h</div>
          </div>
          <div className="bg-chart-4/10 rounded-lg p-3 text-center">
            <div className="text-xs text-chart-4 font-semibold">Planned</div>
            <div className="text-2xl font-bold text-chart-4">{totalPlannedHours.toFixed(1)}h</div>
          </div>
          <div className="bg-chart-2/10 rounded-lg p-3 text-center">
            <div className="text-xs text-chart-2 font-semibold">Logged</div>
            <div className="text-2xl font-bold text-chart-2">{totalActualHours.toFixed(1)}h</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${
            remainingToPlan >= 0 ? 'bg-chart-5/10' : 'bg-destructive/10'
          }`}>
            <div className={`text-xs font-semibold ${
              remainingToPlan >= 0 ? 'text-chart-5' : 'text-destructive'
            }`}>
              To Plan
            </div>
            <div className={`text-2xl font-bold ${
              remainingToPlan >= 0 ? 'text-chart-5' : 'text-destructive'
            }`}>
              {remainingToPlan.toFixed(1)}h
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div>
          <Label className="text-sm font-semibold mb-3 block">Click a date to add/edit hours</Label>
          <div className="grid grid-cols-7 gap-2">
            {/* Weekday headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-xs font-semibold text-muted-foreground text-center py-1">
                {day}
              </div>
            ))}
            
            {/* Calendar dates */}
            {calendarDates.map((date, index) => {
              const dateStr = toDateKey(date) || '';
              const plannedDay = getHoursForDate(date);
              const actualHours = getActualHoursForDate(date);
              const isSelected = selectedDate === dateStr;
              const hasLogged = hasTimeLogged(date);
              
              return (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedDate(dateStr);
                    setHoursForDate(plannedDay?.hours.toString() || '');
                    setDateNotes(plannedDay?.notes || '');
                  }}
                  disabled={isPast(date) && !plannedDay}
                  className={`
                    relative p-2 rounded-lg border-2 transition-all
                    ${isSelected ? 'border-primary/40 bg-primary/10 shadow-md' : 'border-border hover:border-border'}
                    ${isToday(date) ? 'ring-2 ring-primary/40' : ''}
                    ${isPast(date) && !plannedDay ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                    ${isWeekend(date) ? 'bg-muted/40' : 'bg-white'}
                    ${plannedDay ? 'border-chart-4/40 bg-chart-4/10' : ''}
                    ${plannedDay?.completed ? 'border-chart-2/40 bg-chart-2/10' : ''}
                  `}
                >
                  <div className="text-xs font-semibold text-muted-foreground">
                    {date.getDate()}
                  </div>
                  {plannedDay && (
                    <div className="text-xs font-bold text-chart-4 mt-1">
                      {plannedDay.hours}h
                    </div>
                  )}
                  {hasLogged && (
                    <div className="absolute top-1 right-1">
                      <CheckCircle2 className="w-3 h-3 text-chart-2" />
                    </div>
                  )}
                  {actualHours > 0 && (
                    <div className="text-[10px] text-chart-2 font-semibold">
                      {actualHours.toFixed(1)}h
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Editor */}
        {selectedDate && (
          <Card className="bg-primary/10 border-primary/40">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-primary">
                  {formatDateLong(parseDateLike(selectedDate) || new Date())}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDate(null);
                    setHoursForDate('');
                    setDateNotes('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs">Hours to work</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="e.g., 4"
                    value={hoursForDate}
                    onChange={(e) => setHoursForDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Textarea
                    placeholder="What will you work on?"
                    value={dateNotes}
                    onChange={(e) => setDateNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAddHours}
                  className="flex-1"
                  disabled={!hoursForDate}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                {getHoursForDate(parseDateLike(selectedDate) || new Date()) && (
                  <Button
                    variant="destructive"
                    onClick={() => handleRemoveDay(selectedDate)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Planned Days List */}
        {workPlan.plannedDays.length > 0 && (
          <div>
            <Label className="text-sm font-semibold mb-3 block">Planned Schedule</Label>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {workPlan.plannedDays
                .sort((a, b) => (toDateKey(a.date) || '').localeCompare(toDateKey(b.date) || ''))
                .map((day) => {
                  const date = parseDateLike(day.date) || new Date();
                  const progress = day.actualHours 
                    ? Math.min((day.actualHours / day.hours) * 100, 100)
                    : 0;

                  return (
                    <Card
                      key={day.date}
                      className={`${
                        day.completed
                          ? 'bg-chart-2/10 border-chart-2/40'
                          : 'bg-white border-border'
                      } hover:shadow-md transition-shadow`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm">
                                {formatDateLong(date)}
                              </span>
                              {day.completed && (
                                <CheckCircle2 className="w-4 h-4 text-chart-2" />
                              )}
                            </div>
                            {day.notes && (
                              <p className="text-xs text-muted-foreground mb-2">{day.notes}</p>
                            )}
                            <div className="flex items-center gap-3 text-xs">
                              <div>
                                <span className="text-muted-foreground">Planned: </span>
                                <span className="font-bold text-chart-4">{day.hours}h</span>
                              </div>
                              {day.actualHours !== undefined && day.actualHours > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Logged: </span>
                                  <span className="font-bold text-chart-2">
                                    {day.actualHours.toFixed(1)}h
                                  </span>
                                </div>
                              )}
                            </div>
                            {day.actualHours !== undefined && day.actualHours > 0 && (
                              <div className="mt-2">
                                <Progress
                                  value={Math.min(progress, 100)}
                                  className={
                                    progress >= 100
                                      ? 'h-1.5 [&::-webkit-progress-value]:bg-chart-2/10 [&::-moz-progress-bar]:bg-chart-2/10'
                                      : 'h-1.5'
                                  }
                                />
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDay(day.date)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {workPlan.plannedDays.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm">No work planned yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click on calendar dates to add your work schedule
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
